import React, { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const PREVIEW_HORIZONTAL_PADDING = 40;
const MOBILE_BREAKPOINT = 640;
const escapeRegExp = (value = "") => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const buildExcerptPattern = (excerpt = "") => {
  const normalized = String(excerpt || "").trim();
  if (!normalized) return null;
  const tokens = normalized
    .split(/\s+/)
    .map((token) => token.replace(/[.,;:!?()[\]{}"'“”‘’]/g, ""))
    .filter((token) => token.length >= 4)
    .slice(0, 8);
  if (!tokens.length) return null;
  return new RegExp(`(${tokens.map(escapeRegExp).join("|")})`, "ig");
};

const resolvePageWidth = (containerWidth) => {
  if (!containerWidth) return 960;
  return Math.max(280, Math.floor(containerWidth - PREVIEW_HORIZONTAL_PADDING));
};

// Renders a lightweight placeholder until the page scrolls near the viewport, then mounts the
// real <Page/>. Without this, react-pdf decodes every page of the document into a canvas up
// front — for an image-heavy scanned PDF with many pages that exhausts browser memory and
// crashes/freezes the tab.
const LazyPdfPage = ({ pageNumber, pageWidth, isMobileViewport, forceRender, customTextRenderer, testIdPrefix }) => {
  const [shouldRender, setShouldRender] = useState(Boolean(forceRender));
  const nodeRef = useRef(null);

  useEffect(() => {
    if (forceRender) setShouldRender(true);
  }, [forceRender]);

  useEffect(() => {
    if (shouldRender || !nodeRef.current || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldRender(true);
        }
      },
      { rootMargin: "1000px 0px" }
    );
    observer.observe(nodeRef.current);
    return () => observer.disconnect();
  }, [shouldRender]);

  return (
    <div ref={nodeRef}>
      {shouldRender ? (
        <Page
          pageNumber={pageNumber}
          width={pageWidth}
          renderAnnotationLayer={!isMobileViewport}
          renderTextLayer={!isMobileViewport}
          className="mx-auto"
          canvasBackground="white"
          customTextRenderer={customTextRenderer}
        />
      ) : (
        <div
          style={{ width: pageWidth, aspectRatio: "1 / 1.414" }}
          className="mx-auto flex items-center justify-center bg-slate-100 text-xs text-slate-400"
          data-testid={`${testIdPrefix}-pdf-page-${pageNumber}-placeholder`}
        >
          {pageNumber}
        </div>
      )}
    </div>
  );
};

export const PdfDocumentPreview = ({ pdfPreviewUrl, language = "es", focusedPageNumber = null, highlightedExcerpt = "", testIdPrefix = "document-viewer" }) => {
  const containerRef = useRef(null);
  const pageRefs = useRef({});
  const [pageCount, setPageCount] = useState(0);
  const [pageWidth, setPageWidth] = useState(960);
  const [loadError, setLoadError] = useState("");
  const [activeMobilePage, setActiveMobilePage] = useState(1);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const excerptPattern = buildExcerptPattern(highlightedExcerpt);
  const mobilePageNumber = useMemo(() => {
    if (!pageCount) return 1;
    return Math.min(Math.max(activeMobilePage, 1), pageCount);
  }, [activeMobilePage, pageCount]);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const updateWidth = () => {
      const nextWidth = resolvePageWidth(containerRef.current?.clientWidth || 0);
      setPageWidth(nextWidth);
      setIsMobileViewport(window.innerWidth < MOBILE_BREAKPOINT);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerRef.current);
    window.addEventListener("resize", updateWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  useEffect(() => {
    if (!focusedPageNumber || !pageRefs.current[focusedPageNumber]) return;
    if (isMobileViewport) {
      setActiveMobilePage(focusedPageNumber);
      return;
    }
    pageRefs.current[focusedPageNumber].scrollIntoView({ behavior: "smooth", block: "start" });
  }, [focusedPageNumber, isMobileViewport]);

  useEffect(() => {
    if (!focusedPageNumber || !isMobileViewport) return;
    setActiveMobilePage(focusedPageNumber);
  }, [focusedPageNumber, isMobileViewport]);

  const goToPreviousPage = () => setActiveMobilePage((current) => Math.max(1, current - 1));
  const goToNextPage = () => setActiveMobilePage((current) => Math.min(pageCount || 1, current + 1));

  const renderPdfPage = (pageNumber) => (
    <div
      key={`pdf-page-${pageNumber}`}
      ref={(node) => { pageRefs.current[pageNumber] = node; }}
      className={`overflow-hidden rounded-[22px] bg-white p-2 sm:p-3 shadow-[0_20px_45px_rgba(0,0,0,0.22)] ${focusedPageNumber === pageNumber ? "ring-2 ring-primary/60 ring-offset-2 ring-offset-[#101114]" : ""}`}
      data-testid={`${testIdPrefix}-pdf-page-${pageNumber}`}
    >
      <LazyPdfPage
        pageNumber={pageNumber}
        pageWidth={pageWidth}
        isMobileViewport={isMobileViewport}
        forceRender={isMobileViewport || focusedPageNumber === pageNumber || pageNumber === 1}
        testIdPrefix={testIdPrefix}
        customTextRenderer={!isMobileViewport && excerptPattern && focusedPageNumber === pageNumber
          ? ({ str }) => str.replace(excerptPattern, '<mark class="voxara-pdf-text-highlight">$1</mark>')
          : undefined}
      />
    </div>
  );

  return (
    <section
      ref={containerRef}
      className="overflow-hidden rounded-[28px] border border-white/10 bg-[#101114] shadow-[0_18px_50px_rgba(0,0,0,0.35)]"
      data-testid={`${testIdPrefix}-pdf-preview-panel`}
    >
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-4 text-sm text-white/65">
        <div className="min-w-0">
          <span data-testid={`${testIdPrefix}-pdf-preview-status-label`}>
            {language === "es" ? "Vista renderizada del PDF" : "Rendered PDF view"}
          </span>
          {isMobileViewport ? (
            <div className="mt-1 text-xs text-white/45" data-testid={`${testIdPrefix}-pdf-mobile-mode-label`}>
              {language === "es" ? "Modo móvil ligero: una página a la vez" : "Light mobile mode: one page at a time"}
            </div>
          ) : null}
        </div>
        <a
          href={pdfPreviewUrl}
          target="_blank"
          rel="noreferrer"
          className="rounded-full border border-white/15 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-white/10"
          data-testid={`${testIdPrefix}-pdf-open-new-tab-link`}
        >
          {language === "es" ? "Abrir en nueva pestaña" : "Open in new tab"}
        </a>
      </div>

      <div className="max-h-[86vh] overflow-y-auto px-2 py-4 sm:px-4 lg:px-5" data-testid={`${testIdPrefix}-pdf-preview-scroll-region`}>
        <Document
          file={pdfPreviewUrl}
          onLoadSuccess={({ numPages }) => {
            setPageCount(numPages || 0);
            setLoadError("");
          }}
          onLoadError={(error) => setLoadError(error?.message || "PDF load failed")}
          loading={
            <div className="flex min-h-[420px] items-center justify-center rounded-[24px] bg-white text-sm text-slate-500" data-testid={`${testIdPrefix}-pdf-preview-loading`}>
              {language === "es" ? "Cargando PDF…" : "Loading PDF…"}
            </div>
          }
          error={
            <div className="space-y-3 rounded-[24px] bg-white p-6 text-sm text-slate-600" data-testid={`${testIdPrefix}-pdf-preview-error`}>
              <p>{language === "es" ? "No pudimos renderizar el PDF dentro de Voxara." : "We could not render the PDF inside Voxara."}</p>
              <p className="text-xs text-slate-500">{loadError}</p>
              <a href={pdfPreviewUrl} target="_blank" rel="noreferrer" className="inline-flex rounded-full bg-slate-900 px-4 py-2 text-xs font-medium text-white">
                {language === "es" ? "Abrir PDF original" : "Open original PDF"}
              </a>
            </div>
          }
        >
          {isMobileViewport ? (
            <div className="space-y-4" data-testid={`${testIdPrefix}-pdf-mobile-pager`}>
              <div className="flex items-center justify-between gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2" data-testid={`${testIdPrefix}-pdf-mobile-controls`}>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={goToPreviousPage}
                  disabled={mobilePageNumber <= 1}
                  className="h-9 border-white/15 bg-transparent px-3 text-white hover:bg-white/10"
                  data-testid={`${testIdPrefix}-pdf-prev-page-button`}
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  {language === "es" ? "Anterior" : "Prev"}
                </Button>
                <div className="text-center text-xs text-white/70" data-testid={`${testIdPrefix}-pdf-mobile-page-indicator`}>
                  {language === "es" ? `Página ${mobilePageNumber} de ${pageCount || 1}` : `Page ${mobilePageNumber} of ${pageCount || 1}`}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={goToNextPage}
                  disabled={mobilePageNumber >= (pageCount || 1)}
                  className="h-9 border-white/15 bg-transparent px-3 text-white hover:bg-white/10"
                  data-testid={`${testIdPrefix}-pdf-next-page-button`}
                >
                  {language === "es" ? "Siguiente" : "Next"}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
              {renderPdfPage(mobilePageNumber)}
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {Array.from({ length: pageCount }, (_, index) => renderPdfPage(index + 1))}
            </div>
          )}
        </Document>
      </div>
    </section>
  );
};

export default PdfDocumentPreview;